import type { SubscriptionTypes } from "@component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type {
	AbstractLeadCalculatorRequest,
	AbstractLeadCalculatorResponse,
	AbstractLeadFormFields,
	ProductType,
} from "@component/assets/AbstractLeadForm/types";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import "./style.scss";

export interface FormFields extends AbstractLeadFormFields {
	companyName: string;
	companyFoundYear: number;
	netIncome: number;
	operatingProfit?: number;
	equity?: number;
	loanAmount: number;
	taxNumber?: string;
}

/**
 * @Shortcode
 *
 * @title "Vállalati hitel leadform jövedelemmel"
 * @shortcode "sme_loan_lead_with_currency_inputs"
 * @slug "vallalati-hitel-jovedelemmel"
 * @type "leadform"
 */
export interface SmeLoanLeadFormWithCurrencyInputsCalcRequest extends AbstractLeadCalculatorRequest {
	companyName?: string;
	companyFoundYear?: number;
	netIncome?: number;
	operatingProfit?: number;
	equity?: number;
	loanAmount?: number;

	depreciation?: number;
	existingLoan?: number;

	companyTaxNumber?: string;
}

export type SmeLoanLeadFormWithCurrencyInputsCalcResponse = AbstractLeadCalculatorResponse;

type Props = AbstractLeadFormProps<
	SmeLoanLeadFormWithCurrencyInputsCalcRequest,
	SmeLoanLeadFormWithCurrencyInputsCalcResponse
> & {
	hideCityNameField?: boolean;
	withListingLeadformInputs?: boolean;
};

type State = AbstractLeadFormState<FormFields>;

export class SmeLoanLeadFormWithCurrencyInputs extends AbstractLeadForm<
	SmeLoanLeadFormWithCurrencyInputsCalcRequest,
	SmeLoanLeadFormWithCurrencyInputsCalcResponse,
	FormFields,
	Props,
	State
> {
	// EventParams
	protected readonly productTypes: string = "SME_LOAN";
	protected readonly componentType: string = "leadform";
	protected readonly subscriptionTypes: SubscriptionTypes[] = ["CORPORATE_NEWSLETTER"];

	public static defaultProps = {
		title: "Érdeklődj szakértőnknél",
		colorScheme: "light",
		bottomLogo: false,
	};

	protected renderSteps(): ReactNode {
		const { title, request, hideCityNameField, withListingLeadformInputs } = this.props;
		const {
			values: { email },
			submitState,
		} = this.state;

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					title={title}
					hideCityNameField={hideCityNameField}
					withListingLeadformInputs={withListingLeadformInputs}
					button="Érdekel a hitel"
					submitState={submitState}
					withLoanAmount={request?.loanAmount === undefined}
					withCompanyFoundYear={request?.companyFoundYear === undefined}
					crossSellOption
					newsletterOption
				/>
				<Step.SuccessPage
					stepName="success-page"
					userEmail={email}
					subscriptionTypes={this.subscriptionTypes}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		if (target === "success-page") {
			return this.submitLead(target);
		}
		super.onStep(target, withEvent);
	}

	private submitLead(target: string): void {
		this.sendLead()
			.then(() => {
				super.onStep(target, false);
				this.setState({
					submitState: undefined,
				});
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getCalculatorRequest(): SmeLoanLeadFormWithCurrencyInputsCalcRequest {
		const { withListingLeadformInputs } = this.props;
		const request = super.getCalculatorRequest();
		const {
			values: {
				companyName,
				companyFoundYear = request.companyFoundYear,
				loanAmount = request.loanAmount,
				netIncome,
				operatingProfit,
				equity,
				taxNumber,
			},
		} = this.state;

		const calculatorRequest = {
			...request,
			companyName,
			companyFoundYear,
			netIncome,
			operatingProfit,
			equity,
			loanAmount,
			depreciation: 0,
			existingLoan: 0,
		};

		if (withListingLeadformInputs) {
		    calculatorRequest.companyTaxNumber = taxNumber;
		}

		return calculatorRequest;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-sme-loan";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { lastName, firstName },
		} = this.state;

		return {
			lastName,
			firstName,
		};
	}

	protected getProductType(): ProductType {
		return "SME_LOAN";
	}

	protected getClassName(): string {
		return `${super.getClassName()} sme-loan-with_currency_inputs`;
	}

	protected getComponentName(): string {
		return "SmeLoanLeadFormWithCurrencyInputs";
	}
}

export default SmeLoanLeadFormWithCurrencyInputs;
